import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CheckIcon } from 'lucide-react';
interface PillarProps {
  number: number;
  title: string;
  description: string;
  points: string[];
  delay: number;
}
const Pillar = ({
  number,
  title,
  description,
  points,
  delay
}: PillarProps) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  const variants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1 + delay,
        duration: 0.6,
        ease: 'easeOut'
      }
    })
  };
  return <div ref={ref} className="relative mb-16 last:mb-0" data-id="element-203">
      {/* Timeline vertical line */}
      <div className="absolute left-[22px] top-0 bottom-0 w-[2px] bg-gray-700" data-id="element-204" />
      {/* Timeline node */}
      <motion.div initial={{
      scale: 0,
      opacity: 0
    }} animate={inView ? {
      scale: 1,
      opacity: 1
    } : {
      scale: 0,
      opacity: 0
    }} transition={{
      delay: delay,
      duration: 0.4
    }} className="absolute left-0 top-0 w-[45px] h-[45px] rounded-full bg-[#ffdf07] flex items-center justify-center text-black font-bold z-10" data-id="element-205">
        {number}
      </motion.div>
      {/* Content container */}
      <div className="ml-[70px] pt-2" data-id="element-206">
        <motion.h3 custom={0} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={variants} className="text-2xl md:text-3xl text-white mb-4 typekit-heading" data-id="element-207">
          Pillar {number}: {title}
        </motion.h3>
        <motion.p custom={1} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={variants} className="text-gray-300 mb-6 libre-caslon-text-regular leading-relaxed" data-id="element-208">
          {description}
        </motion.p>
        <div className="space-y-3" data-id="element-209">
          {points.map((point, index) => <motion.div key={index} custom={index + 2} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={variants} className="flex items-start" data-id="element-210">
              <div className="text-[#ffdf07] mr-3 mt-1" data-id="element-211">
                <CheckIcon size={18} data-id="element-212" />
              </div>
              <p className="text-gray-300 libre-caslon-text-regular leading-relaxed" data-id="element-213">
                {point}
              </p>
            </motion.div>)}
        </div>
      </div>
    </div>;
};
const TimelineSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="w-full bg-black py-20 px-4" data-id="element-214">
      <div className="max-w-4xl mx-auto" data-id="element-215">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-216">
          <h2 className="text-white text-3xl md:text-4xl mb-6 typekit-heading" data-id="element-217">
            Our Process: The 10-Day Sprint
          </h2>
        </motion.div>
        <div className="relative" data-id="element-218">
          <Pillar number={1} title="Know What You're Worth" description="We analyze your books to establish a defensible price range, show you what similar clinics actually sold for, and give you the 3 fastest moves to raise your price." points={['Comprehensive valuation of your business based on market data', 'Comparative analysis with similar successful exits', 'Strategic roadmap with the 3 fastest ways to increase your valuation']} delay={0.2} data-id="element-219" />
          <Pillar number={2} title="Think Like a Buyer" description="You'll see your numbers exactly as investors do, learn how to defend that price range, and discover how buyers think so you can shut down their tactics." points={['Investor-eye view of your financial performance and metrics', 'Value defense strategy for each aspect of your business', 'Insider knowledge of buyer psychology and negotiation tactics']} delay={0.4} data-id="element-220" />
          <Pillar number={3} title="Defend & Grow Your Value" description="We deliver a personalized game plan to fix value gaps, break down a mock offer so you know what a potential take-home number looks like, and give you customized talk tracks for buyer meetings." points={['Tailored action plan to address value detractors in your business', 'Complete mock offer analysis showing true after-tax proceeds', 'Custom scripts and talking points for high-stakes buyer conversations']} delay={0.6} data-id="element-221" />
        </div>
      </div>
    </section>;
};
export default TimelineSection;